import { useEffect, useState } from "react";
import { Download, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useLocale } from "@/contexts/LocaleContext";

const DISMISS_KEY = "kaffa-pwa-dismissed-at";
const DISMISS_TTL_MS = 1000 * 60 * 60 * 24 * 14; // 14 days

const isStandalone = () =>
  typeof window !== "undefined" &&
  (window.matchMedia?.("(display-mode: standalone)").matches ||
    window.navigator.standalone === true);

export const InstallPrompt = () => {
  const { t } = useLocale();
  const [deferred, setDeferred] = useState(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    if (isStandalone()) return;
    const dismissedAt = Number(localStorage.getItem(DISMISS_KEY) || 0);
    if (dismissedAt && Date.now() - dismissedAt < DISMISS_TTL_MS) return;

    const onBeforeInstall = (e) => {
      e.preventDefault();
      setDeferred(e);
      setVisible(true);
    };
    const onInstalled = () => {
      setVisible(false);
      setDeferred(null);
      localStorage.setItem(DISMISS_KEY, String(Date.now()));
    };

    window.addEventListener("beforeinstallprompt", onBeforeInstall);
    window.addEventListener("appinstalled", onInstalled);
    return () => {
      window.removeEventListener("beforeinstallprompt", onBeforeInstall);
      window.removeEventListener("appinstalled", onInstalled);
    };
  }, []);

  const install = async () => {
    if (!deferred) return;
    try {
      await deferred.prompt();
      const choice = await deferred.userChoice;
      if (choice?.outcome !== "accepted") {
        localStorage.setItem(DISMISS_KEY, String(Date.now()));
      }
    } finally {
      setDeferred(null);
      setVisible(false);
    }
  };

  const dismiss = () => {
    localStorage.setItem(DISMISS_KEY, String(Date.now()));
    setVisible(false);
  };

  if (!visible) return null;

  return (
    <div
      data-testid="pwa-install-banner"
      role="dialog"
      aria-label={t("pwa.install_cta")}
      className="fixed left-3 right-3 sm:left-auto sm:right-6 bottom-4 z-50 max-w-sm sm:w-[380px] border border-border bg-card shadow-2xl"
    >
      <div className="flex items-start gap-3 p-4">
        <div className="shrink-0 h-10 w-10 border border-border bg-secondary flex items-center justify-center">
          <Download className="h-4 w-4 text-accent" strokeWidth={1.5} />
        </div>
        <div className="min-w-0 flex-1">
          <div className="font-mono-data text-[10px] uppercase tracking-[0.25em] text-accent">
            {t("pwa.tagline")}
          </div>
          <div className="font-display text-base tracking-tight mt-1">
            {t("pwa.install_title")}
          </div>
          <p className="text-xs text-muted-foreground mt-1 leading-relaxed">
            {t("pwa.install_subtitle")}
          </p>
          <div className="mt-3 flex items-center gap-2">
            <Button
              onClick={install}
              data-testid="pwa-install-confirm"
              className="h-8 rounded-none bg-primary text-primary-foreground hover:bg-accent hover:text-accent-foreground text-[11px] uppercase tracking-wider font-mono-data px-3"
            >
              {t("pwa.install_cta")}
            </Button>
            <button
              onClick={dismiss}
              data-testid="pwa-install-dismiss"
              className="h-8 px-3 font-mono-data text-[11px] uppercase tracking-wider text-muted-foreground hover:text-foreground transition-colors"
            >
              {t("pwa.dismiss")}
            </button>
          </div>
        </div>
        <button
          onClick={dismiss}
          aria-label="Close"
          className="shrink-0 text-muted-foreground/60 hover:text-foreground transition-colors -mt-1 -mr-1 p-1"
        >
          <X className="h-4 w-4" />
        </button>
      </div>
    </div>
  );
};
